/** Audio plumbing for the live ears: the realtime API takes PCM16 mono at 24 kHz, base64, and nothing else. */

/** Linear resample to 24 kHz. Cheap and good enough for speech (the microphone is band-limited already). */
export function resampleTo24k(samples, inRate) {
  if (!inRate || inRate === 24000) return samples;
  const ratio = inRate / 24000;
  const n = Math.floor(samples.length / ratio);
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(i0 + 1, samples.length - 1);
    out[i] = samples[i0] + (samples[i1] - samples[i0]) * (pos - i0);
  }
  return out;
}

/** Float samples (-1..1) as PCM16 samples. */
export function floatToPcm16(samples) {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    out[i] = s < 0 ? Math.round(s * 32768) : Math.round(s * 32767);
  }
  return out;
}

/** PCM16 samples as little-endian bytes, base64. */
export function pcm16ToBase64(pcm) {
  const bytes = new Uint8Array(pcm.length * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < pcm.length; i++) view.setInt16(i * 2, pcm[i], true);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** Float samples (-1..1) as little-endian PCM16, base64. */
export function floatToPcm16Base64(samples) {
  return pcm16ToBase64(floatToPcm16(samples));
}

/** Chunks of PCM16 mono as one WAV file (a recorded note the clip door can hear), `name` set like a recorded blob's. */
export function pcm16ToWavBlob(chunks, rate = 24000) {
  const n = chunks.reduce((a, c) => a + c.length, 0);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const str = (o, t) => { for (let i = 0; i < t.length; i++) v.setUint8(o + i, t.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE'); str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, n * 2, true);
  let o = 44;
  for (const c of chunks) for (let i = 0; i < c.length; i++, o += 2) v.setInt16(o, c[i], true);
  const blob = new Blob([buf], { type: 'audio/wav' });
  blob.name = 'voice.wav';
  return blob;
}

/** Root-mean-square level of one frame, 0..1. */
export function rmsOf(samples) {
  if (!samples.length) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}
